/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/anchor-has-content */
import { useQuery } from "@tanstack/react-query";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { HEALTH_INDEX_OPTIONS } from "../../../config";
import { FacilityManagementFilterConditionData } from "../../../models/FacilityFilterConditionData";
import { HealthIndexData } from "../../../models/HealthIndexData";
import { InternalLabelData } from "../../../models/InternalLabelData";
import { OrganizationData } from "../../../models/OrganizationData";
import { ProfBooleanData } from "../../../models/ProfBooleanData";
import DataSvc from "../../../services/dataSvc";
import { modalViewGAEvent } from "../../../utils/ga";
import IfOrCondition from "../../FacilityListComponents/IfOrCondition";
import ItemCondition from "../../FacilityListComponents/ItemCondition";
import { ModalConfirm } from "../../ModalConfirm";
import "../../FacilityListComponents/ModalFilterCondition/styles.scss";

export interface IModalFilterConditionProps {
  conditionFormData: FacilityManagementFilterConditionData;
  onClose: () => void;
  onApply: (conditionFormData: FacilityManagementFilterConditionData) => void;
  onClearConditions: () => void;
  showHealthIndex?: boolean;
}

export const ModalFilterCondition: React.FunctionComponent<
  IModalFilterConditionProps
> = (props) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`facilityManagementPage.modalFilterCondition.${key}`);

  const { onClose, onApply, onClearConditions, showHealthIndex } = props;

  const [conditionFormData, setConditionFormData] =
    useState<FacilityManagementFilterConditionData>({
      organizations: [],
      isArchived: undefined,
      healthIndex: [],
      internalLabels: [],
    });

  const [selectAll, setSelectAll] = useState<any>({
    organizations: false,
    internalLabels: false,
  });

  const [organizationsData, setOrganizationsData] = useState<
    OrganizationData[]
  >([]);
  const [internalLabelsData, setInternalLabelsData] = useState<
    (InternalLabelData & { checked?: boolean })[]
  >([]);

  const health = _.cloneDeep(HEALTH_INDEX_OPTIONS);
  health.forEach((item) => {
    item.name = t(`healthIndexLabels.${item.key}`);
  });
  _.intersectionBy(health, props.conditionFormData.healthIndex, "name").forEach(
    (item) => (item.checked = true)
  );

  const [healthIndexData, setHealthIndexData] =
    useState<HealthIndexData[]>(health);

  const [shownModalConfirm, setShownModalConfirm] = useState<boolean>(false); // false

  const [isArchived, setIsArchived] = useState<ProfBooleanData>({
    key: "is_archived",
    shouldInclude: props.conditionFormData.isArchived?.shouldInclude,
  });

  const { data: organizations } = useQuery(["organizations"], () => {
    return DataSvc.getOrganizations(
      _.pickBy({
        sort: "name",
        limit: 1000,
      })
    );
  });

  const { data: internalLabels } = useQuery(["internalLabels"], () =>
    DataSvc.getInternalLabels({
      sort: "id",
    })
  );

  useEffect(() => {
    if (organizations) {
      if (props.conditionFormData.organizations) {
        _.intersectionBy(
          organizations.list,
          props.conditionFormData.organizations,
          "id"
        ).forEach((item) => (item.checked = true));
      }
      setOrganizationsData(organizations.list);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizations]);

  useEffect(() => {
    if (internalLabels && internalLabels.data) {
      let data = internalLabels.data.map((item: InternalLabelData) => ({
        ...item,
        checked: false,
      }));
      if (props.conditionFormData.internalLabels) {
        data = data.map((item: InternalLabelData) => ({
          ...item,
          checked: !!props.conditionFormData.internalLabels?.find(
            (i) => i.id === item.id && i.checked
          ),
        }));
      }
      setInternalLabelsData(data);
    }
  }, [internalLabels, props.conditionFormData.internalLabels]);

  // Send GA event on open
  useEffect(() => {
    modalViewGAEvent("facility-manage");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // change Checkbox
  const changeCheckbox = (value: boolean, index: number, name: string) => {
    switch (name) {
      case "organizations":
        const organizationsDataTemp = organizationsData;
        organizationsDataTemp[index].checked = value;
        setOrganizationsData(_.cloneDeep(organizationsDataTemp));
        break;
      case "healthIndex":
        const healthIndexDataTemp = healthIndexData;
        healthIndexDataTemp[index].checked = value;
        setHealthIndexData(_.cloneDeep(healthIndexDataTemp));
        break;
      case "internalLabels":
        const internalLabelsDataTemp = internalLabelsData;
        internalLabelsDataTemp[index].checked = value;
        setInternalLabelsData(_.cloneDeep(internalLabelsDataTemp));
        break;
    }
  };

  // change Select All
  const changeSelectAll = (value: boolean, name: string) => {
    const selectAllTemp = selectAll;
    switch (name) {
      case "organizations":
        selectAllTemp.organizations = value;
        const organizationsDataTemp = organizationsData;
        organizationsDataTemp.forEach((item) => {
          item.checked = value;
        });
        setOrganizationsData(_.cloneDeep(organizationsDataTemp));
        break;
      case "healthIndex":
        selectAllTemp.healthIndex = value;
        const healthIndexDataTemp = healthIndexData;
        healthIndexDataTemp.forEach((item) => {
          item.checked = value;
        });
        setHealthIndexData(_.cloneDeep(healthIndexDataTemp));
        break;
      case "internalLabels":
        selectAllTemp.internalLabels = value;
        const internalLabelsDataTemp = internalLabelsData;
        internalLabelsDataTemp.forEach((item) => {
          item.checked = value;
        });
        setInternalLabelsData(_.cloneDeep(internalLabelsDataTemp));
        break;
    }
    setSelectAll(_.cloneDeep(selectAllTemp));
  };

  // on Clear
  const onClear = () => {
    changeSelectAll(false, "organizations");
    changeSelectAll(false, "internalLabels");
    onClearConditions();
  };

  const onClickApply = () => {
    const conditionFormDataTemp = conditionFormData;
    if (isArchived.shouldInclude !== undefined) {
      conditionFormDataTemp.isArchived = isArchived;
    }
    conditionFormDataTemp.organizations = organizationsData.filter(function (
      item: OrganizationData
    ) {
      return item.checked;
    });
    conditionFormDataTemp.healthIndex = healthIndexData.filter(function (
      item: HealthIndexData
    ) {
      return item.checked;
    });
    conditionFormDataTemp.internalLabels = internalLabelsData.filter(function (
      item
    ) {
      return item.checked;
    });
    setConditionFormData(_.cloneDeep(conditionFormDataTemp));
    onApply(_.cloneDeep(conditionFormDataTemp));
  };

  // Change archived
  const changeArchived = (key: string, nextValue: boolean | undefined) => {
    setIsArchived({
      key,
      shouldInclude: nextValue,
    });
  };

  return (
    <div className="filter-panel">
      <div className="filter-mains">
        <a
          className="icons btn-close"
          onClick={() => {
            onClose();
          }}
        ></a>
        <div className="top-title flex-grid">
          <div className="titles">{t("filter_condition")}</div>
          <div className="rights">
            <a
              href="#javascript"
              className="btn btn-border"
              onClick={(event) => {
                setShownModalConfirm(true);
                event.preventDefault();
              }}
            >
              {t("clear")}
            </a>
          </div>
        </div>
        <div className="check-full-wrap">
          {isArchived && (
            <div className="check-groups">
              <div className="blue-block-row flex">
                <div className="blue-block">{t("is_archived")}</div>
              </div>
              <div className="check-groups-horizontal">
                <IfOrCondition
                  isBlueTitle={false}
                  item={isArchived}
                  changeCheckbox={changeArchived}
                />
              </div>
            </div>
          )}
          {showHealthIndex && (
            <ItemCondition
              title={t("health_index")}
              checkedAll={selectAll.healthIndex}
              sectionName={"healthIndex"}
              isBlueTitle={true}
              dataList={healthIndexData}
              changeSelectAll={(checked: boolean, sectionName: string) => {
                changeSelectAll(checked, sectionName);
              }}
              changeCheckbox={(
                checked: boolean,
                index: number,
                sectionName: string
              ) => {
                changeCheckbox(checked, index, sectionName);
              }}
            />
          )}
          <ItemCondition
            title={t("internal_labels")}
            checkedAll={selectAll.internalLabels}
            sectionName={"internalLabels"}
            isBlueTitle={true}
            dataList={internalLabelsData}
            changeSelectAll={(checked: boolean, sectionName: string) => {
              changeSelectAll(checked, sectionName);
            }}
            changeCheckbox={(
              checked: boolean,
              index: number,
              sectionName: string
            ) => {
              changeCheckbox(checked, index, sectionName);
            }}
          />
          <ItemCondition
            title={t("organizations")}
            checkedAll={selectAll.organizations}
            sectionName={"organizations"}
            isBlueTitle={true}
            dataList={organizationsData}
            changeSelectAll={(checked: boolean, sectionName: string) => {
              changeSelectAll(checked, sectionName);
            }}
            changeCheckbox={(
              checked: boolean,
              index: number,
              sectionName: string
            ) => {
              changeCheckbox(checked, index, sectionName);
            }}
          />
        </div>
        <div className="bottom-btns">
          <a
            className="btn btn-border"
            onClick={() => {
              onClose();
            }}
          >
            {t("cancel")}
          </a>
          <a
            className="btn btn-blue"
            onClick={(event) => {
              onClickApply();
              event.preventDefault();
            }}
          >
            {t("apply")}
          </a>
        </div>
      </div>
      {shownModalConfirm && (
        <ModalConfirm
          title={"please_confirm_to_reset"}
          cancelLabel={"no-go"}
          confirmLabel={"go"}
          onClose={() => {
            setShownModalConfirm(false);
          }}
          onConfirm={() => {
            onClear();
            setShownModalConfirm(false);
          }}
        />
      )}
    </div>
  );
};

export default ModalFilterCondition;
